/**
 * Select Component
 * 
 * Dropdown select with validation support.
 */

import React, { SelectHTMLAttributes, forwardRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

export interface SelectOption {
  value: string | number;
  label: string;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Label text */
  label: string;
  /** Error message */
  error?: string;
  /** Options list */
  options: SelectOption[];
  /** Placeholder text */
  placeholder?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, options, placeholder = 'Select an option', className = '', ...props }, ref) => {
    // Avoid dual value="" options (disabled placeholder + real empty choice) — browser
    // resolves controlled value="" to the first match, which is the disabled placeholder.
    const hasEmptyOption = options.some(
      (option) => option.value === '' || option.value === null || option.value === undefined,
    );

    return (
      <div className="space-y-1.5">
        {label && (
          <label className="block text-sm font-medium text-gray-700">
            {label}
            {props.required && <span className="text-[#c00035] ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            className={`
              w-full ps-4 pe-10 py-2.5 rounded-xl
              border ${error ? 'border-red-300 focus:ring-red-200 bg-red-50' : 'border-gray-200 focus:ring-primary-500/20 bg-white'}
              text-sm text-gray-900 capitalize
              focus:outline-none focus:ring-2 focus:border-transparent
              transition-all duration-200
              cursor-pointer
              appearance-none [-webkit-appearance:none] bg-none
              disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed
              ${className}
            `}
            {...props}
          >
            {!hasEmptyOption && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={String(option.value)} value={option.value} className="capitalize">
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown
            className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            aria-hidden="true"
          />
        </div>
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm text-[#c00035]"
          >
            {error}
          </motion.p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;

